import { nanoid } from 'nanoid';

import {
  type GridPosition,
  type InteractionSource,
  type SelectionRange,
  type SelectionState
} from '../state/types';

import { type ReducerResponse, type SelectionAxis } from './types';
import { findRangeContainingIndex } from './utils/range/find-range-containing-index';
import { findRangesAdjacentToIndex } from './utils/range/find-ranges-adjacent-to-index';
import { getRangeMinMax } from './utils/range/get-range-min-max';
import {
  createNewSingleItemRange,
  extendAdjacentRange,
  mergeAdjacentRanges
} from './utils/toggle-add';
import {
  removeSingleItemRange,
  shortenRangeAtBoundary,
  splitRangeInMiddle
} from './utils/toggle-remove';

type ToggleParams = {
  axis: SelectionAxis;
  primaryCoord: { index: number };
  source: InteractionSource;
};

/**
 * Handles toggle operations for row or column selections.
 *
 * Toggle behavior:
 * - If the index is already selected: remove it (may shorten, split, or remove a range)
 * - If the index is not selected: add it (may create new range, extend adjacent, or merge ranges)
 */
export function toggleReducer(
  state: SelectionState,
  params: ToggleParams
): ReducerResponse {
  const { primaryCoord, axis } = params;
  const index = primaryCoord.index;
  const targetMode = axis === 'row' ? 'row' : 'column';

  if (state.mode !== targetMode) {
    const position: GridPosition =
      axis === 'row'
        ? { row: { index }, col: { index: 0 } }
        : { row: { index: 0 }, col: { index } };
    const newSelection: SelectionRange = {
      id: nanoid(),
      anchor: position,
      lead: position
    };

    return {
      delta: {
        kind: 'selectionReplaced',
        source: params.source,
        selections: [newSelection],
        focusChange: {
          prev: state.focusedCell,
          next: position
        }
      },
      next: {
        ...state,
        mode: targetMode,
        selections: [newSelection],
        focusedCell: position,
        lastInteractionSource: params.source
      }
    };
  }

  const containingRange = findRangeContainingIndex({
    ranges: state.selections,
    index,
    axis
  });

  //index present in selections, we are deselecting
  if (containingRange) {
    const { min, max } = getRangeMinMax(containingRange, axis);
    const isSingleItem = min === max;
    const isAtStart = index === min;
    const isAtEnd = index === max;

    if (isSingleItem) {
      return removeSingleItemRange({
        state,
        range: containingRange,
        source: params.source,
        mode: targetMode
      });
    }

    if (isAtStart || isAtEnd) {
      return shortenRangeAtBoundary({
        state,
        range: containingRange,
        index,
        source: params.source,
        axis
      });
    }

    return splitRangeInMiddle({
      state,
      range: containingRange,
      index,
      source: params.source,
      axis
    });
  }

  //index not found in selection, we are selecting
  const adjacentRanges = findRangesAdjacentToIndex({
    ranges: state.selections,
    index,
    axis
  });

  if (adjacentRanges.length === 0) {
    return createNewSingleItemRange({
      state,
      axis,
      index,
      source: params.source,
      mode: targetMode
    });
  }

  if (adjacentRanges.length === 1) {
    return extendAdjacentRange({
      state,
      range: adjacentRanges[0],
      axis,
      newIndex: index,
      source: params.source,
      mode: targetMode
    });
  }

  return mergeAdjacentRanges({
    state,
    range1: adjacentRanges[0],
    range2: adjacentRanges[1],
    axis,
    newIndex: index,
    source: params.source,
    mode: targetMode
  });
}
